import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PredefinedChartComponent } from './predefined-chart.component';
import { PredefinedChartsService, PREDEFINED_CHART_DEFINITIONS, BUCKET_GRANULARITY_OPTIONS, DEFAULT_BUCKET_GRANULARITY_HOURS, ChartViewCombinedResponse } from '../../services/predefined-charts.service';
import { SharedDataService } from '../../services/shared-data.service';
import { FilterService } from '../../services/filter.service';
import type { PredefinedChartData, BatchPredefinedChartsResponse, PredefinedChartDefinition } from '../../shared/predefined-charts.types';
import type { FilterRequest } from '../../shared/types';
import { transformTestToFilters } from '../../shared/appUtil';

export interface PredefinedChartsPayload {
  start_date: string;
  end_date: string;
  filters: FilterRequest[];
  timeZone: string;
}

const AREA_CHART_IDS = new Set(['area-primary-topics', 'area-customer-intent']);

@Component({
  selector: 'app-predefined-charts-container',
  standalone: true,
  imports: [CommonModule, PredefinedChartComponent],
  templateUrl: './predefined-charts-container.component.html',
  styleUrl: './predefined-charts-container.component.scss',
})
export class PredefinedChartsContainerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() payload: PredefinedChartsPayload | null = null;

  definitions = PREDEFINED_CHART_DEFINITIONS;
  chartResults: Map<string, { data: PredefinedChartData | null; error: string | null }> = new Map();
  loading = false;
  areaChartsLoading = false;
  overallError: string | null = null;
  expandedChartId: string | null = null;
  bucketGranularityHours = DEFAULT_BUCKET_GRANULARITY_HOURS;
  readonly bucketGranularityOptions = BUCKET_GRANULARITY_OPTIONS;

  private areaChartSubscription: Subscription | null = null;

  private dateSubscription: Subscription | null = null;
  private filterSubscription: Subscription | null = null;
  private timezoneSubscription: Subscription | null = null;
  private loadSubscription: Subscription | null = null;
  private _lastStartDate = '';
  private _lastEndDate = '';
  /** Signature of last loaded payload (dates + timezone + filters) to detect any change and reload charts. */
  private _lastPayloadSignature = '';

  constructor(
    private predefinedChartsService: PredefinedChartsService,
    private sharedDataService: SharedDataService,
    private filterService: FilterService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.definitions = this.predefinedChartsService.getChartDefinitions();
    this.chartResults = new Map(this.definitions.map((d) => [d.id, { data: null, error: null }]));

    if (this.payload?.start_date && this.payload?.end_date) {
      const alreadyLoaded = this._lastStartDate === this.payload.start_date && this._lastEndDate === this.payload.end_date;
      if (!alreadyLoaded) {
        this._lastStartDate = this.payload.start_date;
        this._lastEndDate = this.payload.end_date;
        this.loadAllCharts();
      }
      return;
    }

    this.dateSubscription = this.sharedDataService.getDateRange().subscribe((date) => {
      if (date.startAPIDate && date.endAPIDate) {
        this._lastStartDate = date.startAPIDate;
        this._lastEndDate = date.endAPIDate;
        this.payload = {
          start_date: date.startAPIDate,
          end_date: date.endAPIDate,
          filters: this.getFiltersFromService(),
          timeZone: this.sharedDataService.selectedTimezoneValue || 'UTC',
        };
        this.loadAllCharts();
      }
    });

    this.filterSubscription = this.filterService.getRefreshDataStatus().pipe(
      debounceTime(100)
    ).subscribe((status) => {
      if (status) this.buildPayloadAndLoad();
    });

    this.timezoneSubscription = this.sharedDataService.getTimezoneChange().pipe(
      debounceTime(300)
    ).subscribe(() => this.buildPayloadAndLoad());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const payloadChange = changes['payload'];
    if (!payloadChange || !this.payload?.start_date || !this.payload?.end_date) return;
    const signature = this.getPayloadSignature(this.payload);
    const isFirstPayload = payloadChange.firstChange;
    const payloadChanged = isFirstPayload || signature !== this._lastPayloadSignature;
    if (payloadChanged) {
      this._lastStartDate = this.payload.start_date;
      this._lastEndDate = this.payload.end_date;
      this._lastPayloadSignature = signature;
      this.loadAllCharts();
    }
  }

  /** Build a signature so we reload when date range, timezone, or filters change. */
  private getPayloadSignature(p: PredefinedChartsPayload): string {
    const filtersStr = JSON.stringify(p.filters ?? []);
    return `${p.start_date}|${p.end_date}|${p.timeZone ?? ''}|${filtersStr}`;
  }

  ngOnDestroy(): void {
    this.dateSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
    this.timezoneSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    this.areaChartSubscription?.unsubscribe();
  }

  isSingleDayDateRange(): boolean {
    const start = this.payload?.start_date;
    const end = this.payload?.end_date;
    return !!start && !!end && start === end;
  }

  private isSingleDayPayload(payload: PredefinedChartsPayload): boolean {
    return payload.start_date === payload.end_date;
  }

  isAreaChart(id: string): boolean {
    return AREA_CHART_IDS.has(id);
  }

  onBucketGranularityChange(hours: number): void {
    const parsedHours = Number(hours);
    if (!this.isSingleDayDateRange() || this.bucketGranularityHours === parsedHours) return;
    this.bucketGranularityHours = parsedHours;
    this.loadAreaCharts();
  }

  private buildChartViewBody(payload: PredefinedChartsPayload) {
    const body = {
      start_date: payload.start_date,
      end_date: payload.end_date,
      filters: payload.filters,
      timeZone: payload.timeZone || 'UTC',
    };
    if (this.isSingleDayPayload(payload)) {
      return { ...body, bucket_granularity_hours: this.bucketGranularityHours };
    }
    return body;
  }

  private applyCombinedAreaChartResponse(combinedResponse: ChartViewCombinedResponse | null): void {
    if (combinedResponse) {
      const areaPrimaryData = this.predefinedChartsService.mapCombinedResponseToPredefinedChartData(
        combinedResponse,
        'area_primary_topics'
      );
      const areaIntentData = this.predefinedChartsService.mapCombinedResponseToPredefinedChartData(
        combinedResponse,
        'area_customer_intent'
      );
      if (areaPrimaryData) {
        this.chartResults.set('area-primary-topics', { data: areaPrimaryData, error: null });
      } else {
        this.chartResults.set('area-primary-topics', { data: null, error: 'No data for Primary Topics Over Time' });
      }
      if (areaIntentData) {
        this.chartResults.set('area-customer-intent', { data: areaIntentData, error: null });
      } else {
        this.chartResults.set('area-customer-intent', { data: null, error: 'No data for Customer Intents Over Time' });
      }
    } else {
      this.chartResults.set('area-primary-topics', { data: null, error: 'Chart view data unavailable' });
      this.chartResults.set('area-customer-intent', { data: null, error: 'Chart view data unavailable' });
    }
  }

  loadAreaCharts(): void {
    const payload = this.payload;
    if (!payload?.start_date || !payload?.end_date) return;

    const chartViewBody = this.buildChartViewBody(payload);
    this.areaChartsLoading = true;
    this.cdr.markForCheck();

    this.areaChartSubscription?.unsubscribe();
    this.areaChartSubscription = this.predefinedChartsService.fetchChartViewCombinedData(chartViewBody).subscribe({
      next: (combinedResponse) => {
        this.applyCombinedAreaChartResponse(combinedResponse);
        this.areaChartsLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Chart view combined error:', error);
        this.chartResults.set('area-primary-topics', { data: null, error: 'Chart view data unavailable' });
        this.chartResults.set('area-customer-intent', { data: null, error: 'Chart view data unavailable' });
        this.areaChartsLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private getFiltersFromService(): FilterRequest[] {
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data } = transformTestToFilters(filterDataArray);
    return data;
  }

  private buildPayloadAndLoad(): void {
    const start = this.payload?.start_date || this._lastStartDate;
    const end = this.payload?.end_date || this._lastEndDate;
    if (!start || !end) return;

    this.payload = {
      start_date: start,
      end_date: end,
      filters: this.getFiltersFromService(),
      timeZone: this.sharedDataService.selectedTimezoneValue || 'UTC',
    };
    this.loadAllCharts();
  }

  loadAllCharts(): void {
    const payload = this.payload;
    if (!payload?.start_date || !payload?.end_date) {
      this.overallError = 'Please select a date range.';
      this.cdr.markForCheck();
      return;
    }

    this._lastPayloadSignature = this.getPayloadSignature(payload);
    this._lastStartDate = payload.start_date;
    this._lastEndDate = payload.end_date;
    this.overallError = null;
    this.loading = true;
    this.chartResults = new Map(this.definitions.map((d) => [d.id, { data: null, error: null }]));
    this.cdr.markForCheck();

    const request = this.predefinedChartsService.buildRequest(payload);
    const chartViewBody = this.buildChartViewBody(payload);

    const totalRequests = 4;
    const completed = { count: 0 };
    const maybeDone = (): void => {
      completed.count++;
      if (completed.count === totalRequests) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    };

    const applyBatchResponse = (
      response: BatchPredefinedChartsResponse | null,
      batchEndpoint: 'bar-metrics' | 'distribution-metrics' | 'sankey-metrics'
    ): void => {
      if (!response?.success || !response?.data) {
        const msg = response?.message || `Failed to load ${batchEndpoint}`;
        console.error(`Batch error for ${batchEndpoint}:`, msg);
        this.definitions
          .filter((d) => d.batchEndpoint === batchEndpoint)
          .forEach((def) => this.chartResults.set(def.id, { data: null, error: msg }));
        return;
      }
      this.definitions
        .filter((d) => d.batchEndpoint === batchEndpoint)
        .forEach((definition) => {
          const chartData = response.data[definition.apiKey];
          if (chartData) {
            this.chartResults.set(definition.id, { data: chartData, error: null });
          } else {
            console.warn(`Chart data not found for ${definition.id} with key ${definition.apiKey}`);
            this.chartResults.set(definition.id, { data: null, error: 'Chart data not found in response' });
          }
        });
    };

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = new Subscription();

    // Chart view (area charts) – show as soon as response arrives
    this.loadSubscription.add(
      this.predefinedChartsService.fetchChartViewCombinedData(chartViewBody).subscribe({
        next: (combinedResponse) => {
          this.applyCombinedAreaChartResponse(combinedResponse);
          this.cdr.markForCheck();
          maybeDone();
        },
        error: (error) => {
          console.error('Chart view combined error:', error);
          this.chartResults.set('area-primary-topics', { data: null, error: 'Chart view data unavailable' });
          this.chartResults.set('area-customer-intent', { data: null, error: 'Chart view data unavailable' });
          this.cdr.markForCheck();
          maybeDone();
        },
      })
    );

    const API_DELAY_MS = 20;

    // Bar metrics – show as soon as response arrives
    const barTimeoutId = setTimeout(() => {
      this.loadSubscription?.add(
        this.predefinedChartsService.fetchBatchCharts('bar-metrics', request).subscribe({
          next: (res) => {
            applyBatchResponse(res, 'bar-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
          error: () => {
            applyBatchResponse(null, 'bar-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
        })
      );
    }, 0);

    // Distribution metrics – 20ms after bar-metrics
    const distributionTimeoutId = setTimeout(() => {
      this.loadSubscription?.add(
        this.predefinedChartsService.fetchBatchCharts('distribution-metrics', request).subscribe({
          next: (res) => {
            applyBatchResponse(res, 'distribution-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
          error: () => {
            applyBatchResponse(null, 'distribution-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
        })
      );
    }, API_DELAY_MS);

    // Sankey metrics – 20ms after distribution-metrics
    const sankeyTimeoutId = setTimeout(() => {
      this.loadSubscription?.add(
        this.predefinedChartsService.fetchBatchCharts('sankey-metrics', request).subscribe({
          next: (res) => {
            applyBatchResponse(res, 'sankey-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
          error: () => {
            applyBatchResponse(null, 'sankey-metrics');
            this.cdr.markForCheck();
            maybeDone();
          },
        })
      );
    }, API_DELAY_MS * 2);

    this.loadSubscription.add({
      unsubscribe: () => {
        clearTimeout(barTimeoutId);
        clearTimeout(distributionTimeoutId);
        clearTimeout(sankeyTimeoutId);
      },
    });
  }

  refresh(): void {
    if (this.payload) {
      this.loadAllCharts();
    } else {
      this.buildPayloadAndLoad();
    }
  }

  getChartData(id: string): PredefinedChartData | null {
    return this.chartResults.get(id)?.data ?? null;
  }

  getChartError(id: string): string | null {
    return this.chartResults.get(id)?.error ?? null;
  }

  isLoadingChart(id: string): boolean {
    if (this.isAreaChart(id)) {
      return this.loading || this.areaChartsLoading;
    }
    return this.loading;
  }

  expandChart(chartId: string): void {
    this.expandedChartId = chartId;
    this.cdr.markForCheck();
  }

  goBack(): void {
    this.expandedChartId = null;
    this.cdr.markForCheck();
  }

  getExpandedDefinition(): PredefinedChartDefinition | null {
    if (!this.expandedChartId) return null;
    return this.definitions.find((d) => d.id === this.expandedChartId) ?? null;
  }
}
